/**
 * The one result-poll cadence, shared by the two places that wait for a
 * transcript (#435): the engine's wrap-up loop (`session.ts: pollResult`,
 * which starts the moment somebody presses stop) and the result screen's
 * cold-load loop (`routes/result.tsx`, for a link opened from the email).
 * Two loops, one definition, so the patience they spend can never drift
 * apart.
 *
 * The early window exists because the stop-side pipeline publishes in
 * seconds (#272's 5 s target) while the SSE stream has already been torn
 * down by wrap-up, so polling is the only discovery path the stopping
 * device has. 800 ms rather than the earlier 400 ms: that window only ever
 * buys latency for the screen of whoever pressed stop, and 400 ms cost a
 * ~38-request burst across the 15 s window where 800 ms costs ~19, for a
 * difference the human perception of "saving…" does not notice.
 *
 * Patience is expressed as wall-clock rather than a bare attempt count, so
 * tightening an interval cannot silently shorten how long the poll waits.
 *
 * Polling is the only discovery path during wrap-up, which is why the settled
 * interval stays at 1500 ms rather than crawling: [M] summary generation
 * measured 3.07 s on `ses_3a5594238cb9099037ee4fd2`, and a flatter poll
 * interval would have spent a third of that stage asleep. Either interval
 * is this client's choice, not a promise: `PROTOCOL.md` makes the durable
 * table the buffer and the cursor the contract -- "a consumer that misses a
 * live notification reaches the same state by paging forward" -- so the poll
 * rate is not a protocol surface. The fast window is bounded because its
 * burst is real; past it, nothing is arriving on a human timescale anyway.
 */

/** Settled interval between result polls. */
export const RESULT_POLL_MS = 1500;

/** Interval during the fast window right after stop. */
export const RESULT_POLL_EARLY_MS = 800;

/** How long the fast window lasts. */
export const RESULT_POLL_FAST_MS = 15_000;

/**
 * Total patience before the app stops asking and points at the email --
 * deliberately a duration, never an attempt count (see the module comment).
 */
export const RESULT_POLL_TOTAL_MS = 300_000;

/**
 * Attempts covering the total window at the settled interval: the budget of
 * the cold-load loop, which never runs a fast window.
 */
export const RESULT_POLL_STEADY_ATTEMPTS = Math.ceil(RESULT_POLL_TOTAL_MS / RESULT_POLL_MS);

/**
 * Attempts covering the total window when the first {@link RESULT_POLL_FAST_MS}
 * run at the early interval: the budget of the engine loop that starts at stop.
 */
export const RESULT_POLL_ATTEMPTS =
  Math.ceil(RESULT_POLL_FAST_MS / RESULT_POLL_EARLY_MS) +
  Math.ceil((RESULT_POLL_TOTAL_MS - RESULT_POLL_FAST_MS) / RESULT_POLL_MS);
